"use client";

// Data layer Template WhatsApp — baca/tulis satu key JSON `wa_message_templates`
// di `site_settings`.
//
// ── KENAPA PEMBACA TERPISAH, BUKAN MENUMPANG `fetchSiteSettings()` ───────────
// Kepala `siteSettings.ts` menjelaskan key JSON besar SENGAJA dikeluarkan dari
// `.in("key", …)` fetch bersama, supaya tiap halaman tak menyeret muatan yang
// tak dipakainya. Template ini isinya ±30 pesan; ia hanya dibutuhkan halaman
// yang benar-benar punya tombol WhatsApp.
//
// ── KENAPA HOOK, BUKAN FETCH SAAT TOMBOL DIKLIK ─────────────────────────────
// Tripwire `shareOpensWhatsAppDirectly.test.ts`: URL wa.me WAJIB sudah jadi
// saat `window.open` dipanggil. Handler klik yang ber-`await` kehilangan gestur
// kliknya, dan di browser DALAM-APLIKASI WhatsApp/Instagram tab penampung
// `window.open("")` membuka SITUS INI lagi — tombolnya mendarat di website,
// bukan di WhatsApp (bug live 2026-09-08). Jadi template dimuat saat MOUNT;
// selama ia belum tiba, builder memakai teks bawaan di kode dan tombol tetap
// berfungsi seketika.

import { useEffect, useState } from "react";

import { describeSettingsWriteError } from "@/lib/settingsWriteError";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { assertSettingsSession } from "@/lib/supabase/settingsSession";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import { createTtlPromiseCache } from "@/lib/ttlCache";
import { CLIENT_SETTINGS_TTL_MS } from "@/lib/siteSettingsCachePolicy";
import {
  notifySiteSettingsSaved,
  registerSettingsCacheInvalidator,
} from "@/lib/supabase/siteSettingsSync";
import {
  WA_TEMPLATES_SETTING_KEY,
  parseWaTemplates,
  serializeWaTemplates,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";

/**
 * 10 menit (dulu 60 dtk): teks saja, tak menyentuh uang. Audit PROD 2026-10-30:
 * 1.832 GET dari 296 sesi dalam 8,8 jam. Penyimpanan di tab sendiri membuang
 * cache seketika, tab lain di browser yang sama lewat siaran
 * `registerSettingsCacheInvalidator`; perangkat lain menunggu TTL ini.
 */
const cache = createTtlPromiseCache<WaTemplateOverrides>(
  CLIENT_SETTINGS_TTL_MS.waTemplates
);

/** Buang cache template (semua toko) — dipanggil sesudah menyimpan. */
export function invalidateWaTemplatesCache(): void {
  cache.invalidate();
}
registerSettingsCacheInvalidator(invalidateWaTemplatesCache);

/**
 * Override template milik TOKO INI. Kegagalan apa pun memulangkan `{}` =
 * "pakai teks bawaan kode", bukan melempar: setelan yang tak terbaca tidak
 * boleh mematikan tombol WhatsApp.
 *
 * FAIL-CLOSED terhadap toko (aturan turunan A2): sesi anon lolos cabang ESCAPE
 * RLS dan melihat baris SEMUA toko, jadi tanpa toko ter-resolusi jawabannya
 * KOSONG — bukan query tanpa filter yang bisa memulangkan template toko lain.
 */
export async function fetchWaTemplates(): Promise<WaTemplateOverrides> {
  const db = createBrowserSupabase();
  if (!db) return {};
  const storeId = getClientStoreId();
  if (!storeId) return {};

  return cache.get(storeId, async () => {
    const { data, error } = await db
      .from("site_settings")
      .select("value")
      .eq("store_id", storeId)
      .eq("key", WA_TEMPLATES_SETTING_KEY)
      .maybeSingle();
    if (error) throw error;
    return parseWaTemplates(data?.value ?? null);
  });
}

/**
 * Template siap dioper ke builder pesan. Mulai dari `{}` (= teks bawaan) lalu
 * terisi saat fetch selesai — komponen tak pernah menunggu, dan tombol yang
 * diklik sebelum fetch tiba tetap menghasilkan pesan yang benar.
 */
export function useWaTemplates(): WaTemplateOverrides {
  const [templates, setTemplates] = useState<WaTemplateOverrides>({});

  useEffect(() => {
    let alive = true;
    fetchWaTemplates()
      .then((value) => {
        if (alive) setTemplates(value);
      })
      .catch(() => {
        /* fail-open: biarkan teks bawaan kode yang dipakai */
      });
    return () => {
      alive = false;
    };
  }, []);

  return templates;
}

/**
 * Simpan override toko. `store_id` TIDAK dikirim dari browser — trigger
 * `set_store_id_from_context` yang menstempelnya sebelum deteksi konflik
 * (alasannya di mig 3160). Arbiter konflik WAJIB `(store_id, key)`.
 */
export async function saveWaTemplates(
  map: WaTemplateOverrides
): Promise<void> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap.");

  // Tolak di klien saat sesinya sudah tidak ada — payload tak perlu terbang
  // sebagai anonim hanya untuk ditolak DB dengan teks trigger.
  await assertSettingsSession(db);

  const { error } = await db.from("site_settings").upsert(
    [
      {
        key: WA_TEMPLATES_SETTING_KEY,
        value: serializeWaTemplates(map),
        updated_at: new Date().toISOString(),
      },
    ],
    { onConflict: "store_id,key" }
  );
  if (error) throw new Error(describeSettingsWriteError(error));

  invalidateWaTemplatesCache();
  notifySiteSettingsSaved();
}
